import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';

/**
 * Displays the original source document for an evidence file and navigates to a
 * specific page using the browser's native PDF viewer when possible.
 */
const EvidencePageViewer = ({
  claimId,
  fileId,
  fileName,
  pageNumber,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  useEffect(() => {
    if (!claimId || !fileId) {
      setFileUrl(null);
      setError(null);
      setViewerLoading(false);
      return undefined;
    }

    let mounted = true;

    const fetchFileUrl = async () => {
      setLoading(true);
      setError(null);
      setFileUrl(null);

      try {
        const res = await axios.get(`/claims/${claimId}/files/${fileId}/read`);
        if (!mounted) return;

        if (!res.data?.url) {
          setError('Document preview is not available for this evidence file.');
          return;
        }

        setFileUrl(res.data.url);
        setViewerLoading(true);
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.msg || 'Failed to load the evidence document.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchFileUrl();

    return () => {
      mounted = false;
    };
  }, [claimId, fileId]);

  useEffect(() => {
    if (!fileUrl) return;
    setViewerLoading(true);
  }, [fileUrl, pageNumber]);

  const viewerUrl = useMemo(() => {
    if (!fileUrl) return null;

    const hash = pageNumber
      ? `#page=${pageNumber}&zoom=page-width`
      : '';
    return `${fileUrl}${hash}`;
  }, [fileUrl, pageNumber]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0 text-muted">Loading evidence document...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mb-0">
        <Alert.Heading>Evidence Not Available</Alert.Heading>
        <p className="mb-0">{error}</p>
      </Alert>
    );
  }

  if (!viewerUrl) {
    return (
      <Alert variant="light" className="mb-0">
        No evidence document is available for preview.
      </Alert>
    );
  }

  return (
    <div
      className="dbq-evidence-page-viewer"
      style={{
        position: 'relative',
        height: '100%',
        minHeight: 0,
        background: '#f8f9fa',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      {viewerLoading ? (
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(248,249,250,0.9)',
            zIndex: 1,
          }}
        >
          <Spinner animation="border" variant="secondary" />
          <div className="small text-muted mt-3">
            {`Loading ${fileName || 'evidence file'} page ${pageNumber || 1}...`}
          </div>
        </div>
      ) : null}

      <iframe
        key={viewerUrl}
        src={viewerUrl}
        title={`${fileName || 'Evidence file'} page ${pageNumber || 1}`}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
          background: '#ffffff',
        }}
        onLoad={() => setViewerLoading(false)}
      />
    </div>
  );
};

export default EvidencePageViewer;
